/**
 * **图变更的合并** —— 我们自己实现（原来用 `@vue-flow/core` 的
 * `applyNodeChanges` / `applyEdgeChanges`）。
 *
 * ## 为什么自己写（这一步换来的东西）
 *
 * `documentSync.ts` 的文件头记着一段实测：库那两版函数**对 `select`
 * 是就地改**（返回同一个数组、同一个对象），宿主因此必须再补一层
 * 「按 id 克隆」才能让 Vue 看见变化。也就是说：
 *
 * ```text
 *   我们要的语义：   合并变更 → **保证引用变化**（多窗口靠它同步）
 *   库给的语义：     合并变更 → 有时就地改，得由调用方补救
 * ```
 *
 * 自己实现之后这件事变成**函数的契约**，而不是调用方的补偿动作：
 *
 *   · 空变更 → 原样返回（不做无意义的重建）；
 *   · 有变更 → **一定是新数组**，被碰到的元素**一定是新对象**，
 *     没被碰到的**复用**（拖动时每帧都走这里，整份克隆是白开销）。
 *
 * ⚠️ 顺带把「我们到底支持哪几种变更」写清楚了（下面那张 `type` 表）——
 * 以前那是库的内部细节，换引擎时没人说得清我们依赖了哪几种。
 *
 * ## ⚠️ 拖动中的位置变更**不走这里**
 *
 * 拖拽主体是引擎 store 里的那个对象，每帧换引用会把它换掉（实测「节点
 * 完全拖不动了」）。所以宿主对「拖动中的 position」是**就地写**的，
 * 判据见 `verify:document-sync` ⑤ 段。这个模块只负责"松手之后收敛"。
 */
import type { FlowEdge, FlowNode } from './flowTypes'

/**
 * 一次节点变更。
 *
 * ⚠️ 写成**一个宽松的接口**而不是联合类型：调用方拿到的是引擎给的
 * 变更对象，字段齐不齐由引擎决定（`position` / `dimensions` 在库里都是
 * 可选的）。写成严格联合会让"引擎的类型能不能交给它"变成编译期问题，
 * 而这一层的职责恰恰是**容忍形状**。
 */
export interface FlowNodeChange {
  /** 支持这几种（就是我们真的会收到的那几种） */
  type: 'select' | 'position' | 'dimensions' | 'remove' | 'add' | 'replace'
  id?: string
  selected?: boolean
  position?: { x: number; y: number }
  dragging?: boolean
  dimensions?: { width?: number; height?: number }
  resizing?: boolean
  /** `add` / `replace` 带的新节点 */
  item?: FlowNode
}

/** 一次连线变更（比节点少两种：连线没有尺寸和位置） */
export interface FlowEdgeChange {
  type: 'select' | 'remove' | 'add' | 'replace'
  id?: string
  selected?: boolean
  item?: FlowEdge
}

/**
 * 把节点变更合并进一份节点表。
 *
 * ⚠️ **两条契约**（调用方依赖它们，`verify:document-sync` 钉着）：
 *   ① 空变更 → **原样返回同一个数组**；
 *   ② 有变更 → 新数组 + 被碰到的元素是新对象 + 其余元素**复用原对象**。
 */
export function applyNodeChanges(
  changes: readonly FlowNodeChange[],
  nodes: readonly FlowNode[],
): FlowNode[] {
  if (changes.length === 0) return nodes as FlowNode[]

  /** 每个 id 累积到的字段变更（同一个节点被改多次时合并，只克隆一次） */
  const patches = new Map<string, Partial<FlowNode>>()
  const removed = new Set<string>()
  /** `add` / `replace` 带进来的新节点，按出现顺序 */
  const incoming: FlowNode[] = []

  const patch = (id: string | undefined, fields: Partial<FlowNode>): void => {
    if (!id) return
    patches.set(id, { ...(patches.get(id) ?? {}), ...fields })
  }

  for (const change of changes) {
    switch (change.type) {
      case 'select':
        patch(change.id, { selected: change.selected })
        break
      case 'position':
        /* 没有 position 的 position 变更 = 只报"拖完了"，没有值可写 */
        if (change.position) patch(change.id, { position: change.position })
        break
      case 'dimensions':
        /* 引擎量出来的尺寸：有就记，没有就只当"碰过"（换引用） */
        patch(change.id, change.dimensions ? { dimensions: change.dimensions } : {})
        break
      case 'remove':
        if (change.id) removed.add(change.id)
        break
      case 'add':
      case 'replace':
        if (change.item) incoming.push(change.item)
        break
    }
  }

  /* 先按 patches / removed 重建，再把 incoming 合进去 */
  const out: FlowNode[] = []
  for (const node of nodes) {
    if (removed.has(node.id)) continue
    const fields = patches.get(node.id)
    if (fields) {
      out.push({ ...node, ...fields })
      continue
    }
    out.push(node)
  }

  for (const item of incoming) {
    const at = out.findIndex((node) => node.id === item.id)
    /* `replace` 是替换、`add` 撞上同 id 也当替换 —— 不重复放两个同 id 的节点 */
    if (at >= 0) out[at] = item
    else out.push(item)
  }

  /*
   * ⚠️ 兜底：变更里提到的 id 如果一个都没匹配上（文档刚被换过、
   * 或者变更来自另一个窗口），这里仍然要**换数组** ——
   * 调用方就是靠"数组变了"来判断该不该重新发布。
   * 空转的 patches 不算数：那种情况下 `out` 里每个元素都是原对象。
   */
  return out.length === nodes.length && out.every((node, i) => node === nodes[i])
    ? /* 没有任何元素被改动：仍然换数组，但元素复用（引用变化是契约） */
      [...out]
    : out
}

/** 连线版本，语义同上（少 dimensions / position 两种） */
export function applyEdgeChanges(
  changes: readonly FlowEdgeChange[],
  edges: readonly FlowEdge[],
): FlowEdge[] {
  if (changes.length === 0) return edges as FlowEdge[]

  const patches = new Map<string, Partial<FlowEdge>>()
  const removed = new Set<string>()
  const incoming: FlowEdge[] = []

  for (const change of changes) {
    switch (change.type) {
      case 'select':
        if (change.id) patches.set(change.id, { selected: change.selected })
        break
      case 'remove':
        if (change.id) removed.add(change.id)
        break
      case 'add':
      case 'replace':
        if (change.item) incoming.push(change.item)
        break
    }
  }

  const out: FlowEdge[] = []
  for (const edge of edges) {
    if (removed.has(edge.id)) continue
    const fields = patches.get(edge.id)
    out.push(fields ? { ...edge, ...fields } : edge)
  }
  for (const item of incoming) {
    const at = out.findIndex((edge) => edge.id === item.id)
    if (at >= 0) out[at] = item
    else out.push(item)
  }

  return out.length === edges.length && out.every((edge, i) => edge === edges[i]) ? [...out] : out
}
